import React from 'react';
import PropTypes from 'prop-types';

import {LoadingSpinner} from '@albert-io/atomic';
import {refreshSession} from '@albert-io/mandark/authentication';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {UserModelV2} from 'resources/augmented/User/UserModel.v2';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import {
  hasCompletedOnboarding,
  getActiveOnboardingComponentForUser,
  isUserOnFinalStep
} from 'lib/OnboardingUtil/OnboardingUtil';
import NewStudentOnboarding from 'client/SignUp/NewSignUp/Onboarding/Student/NewStudentOnboarding';
import NewEducatorOnboarding from 'client/SignUp/NewSignUp/Onboarding/Educator/NewEducatorOnboarding';
import {SIGNUP_TASKS} from 'client/SignUp/NewSignUp/Onboarding/onboarding.types';
import sessionStore from 'client/Session/SessionStore';
import {invalidateInterest} from 'resources/mandark.resource';
import {redirect} from 'client/User/UserRedirectUtil';
import {history} from 'client/history';

export const onboardingPropTypes = {
  user: PropTypes.instanceOf(UserModelV2).isRequired,
  onComplete: PropTypes.func.isRequired
};

function getUserQuery() {
  return getBaseUserQuery(sessionStore.getUserId()).include('standard_set_creators_v1');
}

const EDUCATOR_ONLY_TASKS = [
  SIGNUP_TASKS.CONTENT_PERSONALIZATION,
  SIGNUP_TASKS.HOMESCHOOL_SCHOOL_SETUP
];

function isEducatorFlow(pendingTasks) {
  return pendingTasks.some((task) => EDUCATOR_ONLY_TASKS.includes(task));
}

/**
 * Renders onboarding steps from `OnboardingUtil` in sequence until the user's onboarding step list
 * indicates they have all been completed.
 *
 * When useNewOnboardingFlow is enabled, routes based on pendingSignupTasks from the GraphQL API.
 */
class ForceOnboarding extends React.Component {
  static propTypes = {
    user: PropTypes.instanceOf(UserModelV2).isRequired,
    onFinalStepComplete: PropTypes.func,
    useNewOnboardingFlow: PropTypes.bool,
    pendingSignupTasks: PropTypes.arrayOf(PropTypes.string),
    signupComplete: PropTypes.bool,
    pendingTasksLoading: PropTypes.bool,
    children: PropTypes.node
  };

  static defaultProps = {
    pendingSignupTasks: [],
    signupComplete: false,
    pendingTasksLoading: false,
    onFinalStepComplete: () => {
      refreshSession().then(() => {
        const {pathname} = history.getCurrentLocation();
        global.scroll(0, 0);
        if (pathname === '/') {
          redirect();
        }
      });
    }
  };

  render() {
    const onComplete = () => {
      invalidateInterest(getUserQuery().done());
    };

    // New flow: use pendingSignupTasks from GraphQL to determine routing
    if (this.props.useNewOnboardingFlow) {
      if (this.props.pendingTasksLoading) {
        return (
          <div className='flex h-screen items-center justify-center'>
            <LoadingSpinner size={3} />
          </div>
        );
      }

      // If signup is complete via the new system, render the app
      if (this.props.signupComplete) {
        return this.props.children;
      }

      const pendingTasks = this.props.pendingSignupTasks;
      const isAccountTypeSelectionPending = pendingTasks.includes(
        SIGNUP_TASKS.ACCOUNT_TYPE_SELECTION
      );

      // While `account_type_selection` is still pending, the user's type isn't decided yet: Show the spinner to avoid routing on this transient state.
      if (isAccountTypeSelectionPending) {
        return (
          <div className='flex h-screen items-center justify-center'>
            <LoadingSpinner size={3} />
          </div>
        );
      }

      if (pendingTasks.length > 0) {
        const onboardingProps = {
          user: this.props.user,
          onComplete,
          onFinalStepComplete: this.props.onFinalStepComplete
        };

        if (isEducatorFlow(pendingTasks)) {
          return <NewEducatorOnboarding {...onboardingProps} />;
        }
        // Educator flows always have an EDUCATOR_ONLY token until `signupComplete` flips, so any
        // remaining state — including the shared-tokens-only case belongs to the student flow.
        return <NewStudentOnboarding {...onboardingProps} />;
      }
    }

    // Legacy flow: use onboarding_steps array on the user model
    if (!hasCompletedOnboarding(this.props.user)) {
      return getActiveOnboardingComponentForUser(this.props.user)({
        user: this.props.user,
        onComplete: isUserOnFinalStep(this.props.user) ? this.props.onFinalStepComplete : onComplete
      });
    }

    return this.props.children;
  }
}

export default awaitMandarkQueries(
  () => ({
    queries: {
      user: getUserQuery()
    }
  }),
  ForceOnboarding
);
